(function () {
  if (window.CustomizerSwatchRail) return;

  const SWIPER_SELECTOR = '[data-swiper]';
  const SLIDE_SELECTOR = '[data-swiper-slide]';
  const DRAG_THRESHOLD = 8;
  const MOBILE_PREVIEW_DURATION = 1200;
  const PREVIEW_PRELOAD_COUNT = 6;
  const stateMap = new WeakMap();
  const previewImageCache = new Map();
  const preview = createPreview();
  let hideTimer = null;
  let previewRequestToken = 0;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function prefersDesktopLayout() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  function isSmallPhoneLayout() {
    return window.matchMedia('(max-width: 480px)').matches;
  }

  function hasHoverPreview() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function getGap(track) {
    const styles = window.getComputedStyle(track);
    return parseFloat(styles.columnGap || styles.gap || '12') || 12;
  }

  function getContainer(swiper) {
    const track = swiper.querySelector('[data-swiper-track]');
    return track ? track.parentElement : null;
  }

  function getMaxScroll(container) {
    return Math.max(0, container.scrollWidth - container.clientWidth);
  }

  function createPreview() {
    let element = document.querySelector('.customizer-swatch-preview');

    if (!element) {
      element = document.createElement('div');
      element.className = 'customizer-swatch-preview';
      element.innerHTML = '<div class="customizer-swatch-preview__frame"></div>';
      document.body.appendChild(element);
    }

    return element;
  }

  function clearHideTimer() {
    if (!hideTimer) return;
    clearTimeout(hideTimer);
    hideTimer = null;
  }

  function hidePreview(immediate) {
    previewRequestToken += 1;
    clearHideTimer();

    if (immediate) {
      preview.classList.remove('is-visible', 'is-below');
      return;
    }

    hideTimer = window.setTimeout(() => {
      preview.classList.remove('is-visible', 'is-below');
    }, 50);
  }

  function getPreviewPayload(slide) {
    if (!slide) return null;

    const previewData = slide.querySelector('[class$="__preview"]');
    if (!previewData) return null;

    return {
      imageUrl: previewData.dataset.previewImg || '',
      background: previewData.dataset.previewBg || '',
      alt: previewData.dataset.previewAlt || '',
    };
  }

  function setPreviewContent(payload) {
    const frame = preview.querySelector('.customizer-swatch-preview__frame');
    if (!frame || !payload) return false;

    if (payload.imageUrl) {
      frame.innerHTML = '<img src="' + payload.imageUrl + '" alt="' + payload.alt + '">';
      return true;
    }

    if (payload.background) {
      frame.innerHTML = '<div class="customizer-swatch-preview__color" style="background:' + payload.background + '"></div>';
      return true;
    }

    return false;
  }

  function ensurePreviewImage(url) {
    if (!url) return Promise.resolve(false);

    const cached = previewImageCache.get(url);
    if (cached === true) return Promise.resolve(true);
    if (cached && typeof cached.then === 'function') return cached;

    const image = new Image();
    image.decoding = 'async';

    const promise = new Promise((resolve) => {
      let settled = false;

      const finish = (success) => {
        if (settled) return;
        settled = true;
        previewImageCache.set(url, success);
        resolve(success);
      };

      const markLoaded = () => {
        if (typeof image.decode === 'function') {
          image.decode().catch(() => {}).then(() => finish(true));
          return;
        }

        finish(true);
      };

      image.addEventListener('load', markLoaded, { once: true });
      image.addEventListener(
        'error',
        () => {
          finish(false);
        },
        { once: true }
      );

      image.src = url;

      if (image.complete && image.naturalWidth > 0) {
        markLoaded();
      }
    });

    previewImageCache.set(url, promise);
    return promise;
  }

  function primeSwiperAssets(swiper) {
    if (!swiper) return;

    swiper.querySelectorAll('[class$="__image"]').forEach((image) => {
      image.loading = 'eager';
      image.decoding = 'async';

      if (!image.complete && typeof image.decode === 'function') {
        image.decode().catch(() => {});
      }
    });

    Array.from(swiper.querySelectorAll(SLIDE_SELECTOR))
      .slice(0, PREVIEW_PRELOAD_COUNT)
      .forEach((slide) => {
        const payload = getPreviewPayload(slide);
        if (payload && payload.imageUrl) {
          ensurePreviewImage(payload.imageUrl);
        }
      });
  }

  function isElementVisible(element) {
    return !!(element && element.getClientRects && element.getClientRects().length);
  }

  function positionPreview(anchorRect) {
    const previewWidth = window.innerWidth < 750 ? 156 : 188;
    const previewHeight = previewWidth + 14;
    const gap = 10;
    const margin = 12;
    const halfWidth = previewWidth / 2;
    let left = anchorRect.left + anchorRect.width / 2;
    let top = anchorRect.top - previewHeight - gap;
    let isBelow = false;

    left = clamp(left, halfWidth + margin, window.innerWidth - halfWidth - margin);

    if (top < 20) {
      top = anchorRect.bottom + gap;
      isBelow = true;
    }

    preview.style.left = left + 'px';
    preview.style.top = top + 'px';
    preview.classList.toggle('is-below', isBelow);
  }

  function showPreviewForSlide(state, slide, options) {
    const payload = getPreviewPayload(slide);
    if (!payload) return;

    clearHideTimer();
    const requestToken = ++previewRequestToken;

    const anchorRect =
      options && options.anchor === 'swiper'
        ? state.swiper.getBoundingClientRect()
        : slide.getBoundingClientRect();

    const revealPreview = () => {
      if (requestToken !== previewRequestToken) return;
      if (!setPreviewContent(payload)) return;

      positionPreview(anchorRect);
      preview.classList.add('is-visible');

      if (options && options.autoHide) {
        hideTimer = window.setTimeout(() => {
          preview.classList.remove('is-visible', 'is-below');
        }, MOBILE_PREVIEW_DURATION);
      }
    };

    if (payload.imageUrl) {
      ensurePreviewImage(payload.imageUrl).then((loaded) => {
        if (!loaded) return;
        revealPreview();
      });
      return;
    }

    revealPreview();
  }

  function updateArrowState(state) {
    const maxScroll = getMaxScroll(state.container);
    const scrollLeft = state.container.scrollLeft;

    if (state.prevButton) {
      state.prevButton.disabled = scrollLeft <= 1;
    }

    if (state.nextButton) {
      state.nextButton.disabled = scrollLeft >= maxScroll - 1;
    }
  }

  function scheduleArrowState(state) {
    if (state.scrollFrame) return;

    state.scrollFrame = window.requestAnimationFrame(() => {
      state.scrollFrame = null;
      updateArrowState(state);
    });
  }

  function updateMetrics(state) {
    const gap = getGap(state.track);
    const visibleCount = prefersDesktopLayout() ? 5 : isSmallPhoneLayout() ? 3 : 4;
    const containerWidth = state.container.clientWidth;

    state.swiper.style.setProperty('--customizer-swatch-gap', gap + 'px');

    if (!containerWidth) {
      updateArrowState(state);
      return;
    }

    const slideWidth = Math.max(0, Math.floor((containerWidth - gap * (visibleCount - 1)) / visibleCount));
    state.swiper.style.setProperty('--customizer-swatch-slide-size', slideWidth + 'px');
    updateArrowState(state);
  }

  function scrollToPosition(state, left, behavior) {
    state.container.scrollTo({
      left: clamp(left, 0, getMaxScroll(state.container)),
      behavior: behavior || 'smooth',
    });
  }

  function scrollByStep(state, direction) {
    const slide = state.slides[0];
    if (!slide) return;

    const gap = getGap(state.track);
    const step = slide.offsetWidth + gap;
    scrollToPosition(state, state.container.scrollLeft + direction * step, 'smooth');
  }

  function bindNavButton(state, button, direction) {
    if (!button) return;

    const handleActivate = (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      scrollByStep(state, direction);
    };

    button.addEventListener('click', handleActivate);

    button.addEventListener(
      'touchend',
      (event) => {
        handleActivate(event);
      },
      { passive: false }
    );

    button.addEventListener('dblclick', (event) => {
      event.preventDefault();
      event.stopPropagation();
    });
  }

  function bindPreviewHandlers(state, slide) {
    const label = slide.querySelector('label');
    if (!label) return;

    label.addEventListener('mouseenter', () => {
      if (!hasHoverPreview()) return;
      showPreviewForSlide(state, slide, { anchor: 'slide' });
    });

    label.addEventListener('mouseleave', () => {
      if (!hasHoverPreview()) return;
      hidePreview(false);
    });

    label.addEventListener('click', () => {
      if (hasHoverPreview()) return;
      if (performance.now() < state.suppressClickUntil) return;

      window.setTimeout(() => {
        showPreviewForSlide(state, slide, { anchor: 'slide', autoHide: true });
      }, 0);
    });
  }

  function init(swiper) {
    if (!swiper) return null;
    if (stateMap.has(swiper)) return stateMap.get(swiper);

    const track = swiper.querySelector('[data-swiper-track]');
    const container = getContainer(swiper);
    const slides = Array.from(swiper.querySelectorAll(SLIDE_SELECTOR));

    if (!track || !container || !slides.length) return null;

    const state = {
      swiper,
      track,
      container,
      slides,
      prevButton: swiper.querySelector('[data-swiper-prev]'),
      nextButton: swiper.querySelector('[data-swiper-next]'),
      pointerStart: null,
      suppressClickUntil: 0,
      scrollFrame: null,
      resizeObserver: null,
    };

    stateMap.set(swiper, state);

    bindNavButton(state, state.prevButton, -1);
    bindNavButton(state, state.nextButton, 1);

    container.addEventListener(
      'scroll',
      () => {
        scheduleArrowState(state);
        hidePreview(true);
      },
      { passive: true }
    );

    swiper.addEventListener(
      'pointerdown',
      (event) => {
        if (event.button && event.button !== 0) return;
        state.pointerStart = {
          x: event.clientX,
          y: event.clientY,
        };
      },
      { passive: true }
    );

    swiper.addEventListener(
      'pointermove',
      (event) => {
        if (!state.pointerStart) return;

        const deltaX = event.clientX - state.pointerStart.x;
        const deltaY = event.clientY - state.pointerStart.y;

        if (Math.abs(deltaX) > DRAG_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY)) {
          state.suppressClickUntil = performance.now() + 150;
          hidePreview(true);
        }
      },
      { passive: true }
    );

    const clearPointerState = () => {
      state.pointerStart = null;
    };

    swiper.addEventListener('pointerup', clearPointerState, { passive: true });
    swiper.addEventListener('pointercancel', clearPointerState, { passive: true });
    swiper.addEventListener('pointerleave', clearPointerState, { passive: true });

    swiper.addEventListener(
      'click',
      (event) => {
        if (performance.now() >= state.suppressClickUntil) return;

        if (event.target.closest(SLIDE_SELECTOR)) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );

    slides.forEach((slide) => {
      bindPreviewHandlers(state, slide);

      const input = slide.querySelector('input[type="radio"]');

      if (input) {
        input.addEventListener('change', () => {
          if (!hasHoverPreview()) {
            showPreviewForSlide(state, slide, { anchor: 'slide', autoHide: true });
          }
        });
      }
    });

    if (typeof ResizeObserver !== 'undefined') {
      state.resizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
          updateMetrics(state);
        });
      });
      state.resizeObserver.observe(container);
    } else {
      window.addEventListener(
        'resize',
        () => {
          updateMetrics(state);
          hidePreview(true);
        },
        { passive: true }
      );
    }

    updateMetrics(state);
    return state;
  }

  function refresh(root) {
    if (!root) {
      document.querySelectorAll(SWIPER_SELECTOR).forEach((swiper) => {
        const state = init(swiper);
        if (state) {
          updateMetrics(state);
          if (isElementVisible(swiper)) {
            primeSwiperAssets(swiper);
          }
        }
      });
      return;
    }

    if (root.matches && root.matches(SWIPER_SELECTOR)) {
      const state = init(root);
      if (state) {
        updateMetrics(state);
        if (isElementVisible(root)) {
          primeSwiperAssets(root);
        }
      }
      return;
    }

    root.querySelectorAll(SWIPER_SELECTOR).forEach((swiper) => {
      const state = init(swiper);
      if (state) {
        updateMetrics(state);
        if (isElementVisible(swiper)) {
          primeSwiperAssets(swiper);
        }
      }
    });
  }

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!event.target.closest(SLIDE_SELECTOR) && !event.target.closest('.customizer-swatch-preview')) {
        hidePreview(true);
      }
    },
    true
  );

  document.addEventListener('DOMContentLoaded', () => {
    refresh(document);
  });

  document.addEventListener('shopify:section:load', (event) => {
    refresh(event.target);
  });

  window.CustomizerSwatchRail = {
    init,
    refresh,
    primeSwiperAssets,
    hidePreview: () => hidePreview(true),
  };
})();
